/**
 * Aggiornamento automatico dell'interfaccia (guida 30, § 4).
 *
 * Implementazione di riferimento: ogni app la copia così com'è e cambia solo `appId`.
 * Non dipende da React: il componente chiama `start()` al mount, legge `notes()` per il riquadro
 * «Aggiornato alla versione…» e chiama `dismissNotes()` sul pulsante «Ho capito».
 *
 * Regole:
 * - appena `/version.json` dichiara una versione diversa dal bundle, la scheda si ricarica da sola:
 *   nessun dialog di conferma, nessun pulsante «Aggiorna»;
 * - la scoperta si diffonde via BroadcastChannel ed evento `storage`: tutte le schede dello stesso
 *   browser con il bundle vecchio si ricaricano, ognuna eseguendo `beforeUpdate` (memento, bozze…);
 * - le note della versione restano in localStorage (`<app>.release.notes`) e si mostrano dopo il
 *   ricaricamento, finché l'utente non le chiude; chiuse in una scheda, si chiudono in tutte;
 * - controlli su avvio, focus, visibilità, rete e ogni minuto mentre la pagina è visibile;
 * - niente loop: se il server consegna ancora il bundle vecchio, una sola ricarica per scheda e per
 *   versione ogni 5 minuti; nel frattempo la scheda resta com'è e riprova al prossimo evento.
 */

export interface VersionDocument {
  version: string;
  title?: string;
  [key: string]: unknown;
}

export interface ReleaseSyncOptions {
  /** Prefisso delle chiavi e nome del canale, es. `mail`. */
  appId: string;
  /** Versione incorporata nel bundle (`__APP_VERSION__`). */
  bundleVersion: string;
  /** Eseguito subito prima di ogni ricarica (salvataggio memento, bozze…). */
  beforeUpdate?(): void;
  /** Chiamato quando le note cambiano (chiuse in un'altra scheda). */
  onNotesChange?(notes: VersionDocument | undefined): void;
  /** Percorso del file di versione, di default `/version.json`. */
  versionUrl?: string;
}

const RELOAD_GRACE_MS = 5 * 60_000;
const CHECK_DEDUP_MS = 30_000;

function read(area: () => Storage, key: string): string | null {
  try {
    return area().getItem(key);
  } catch {
    return null;
  }
}

function write(area: () => Storage, key: string, value: string): void {
  try {
    area().setItem(key, value);
  } catch {
    /* storage non disponibile: l'aggiornamento funziona lo stesso, solo non condiviso */
  }
}

function remove(area: () => Storage, key: string): void {
  try {
    area().removeItem(key);
  } catch {
    /* niente da pulire */
  }
}

export function createReleaseSync(options: ReleaseSyncOptions) {
  const { appId, bundleVersion } = options;
  const keys = {
    notes: `${appId}.release.notes`,
    acknowledged: `${appId}.release.acknowledged`,
    lastCheck: `${appId}.release.lastCheck`,
    reloadedFor: `${appId}.release.reloadedFor`,
  };
  const local = () => localStorage;
  const session = () => sessionStorage;
  let channel: BroadcastChannel | undefined;
  let stopped = false;
  let acknowledgedInMemory = false;

  /** True se questa scheda si è già ricaricata per `version` da poco e il server consegna ancora il bundle vecchio. */
  function reloadedRecently(version: string): boolean {
    try {
      const mark = JSON.parse(read(session, keys.reloadedFor) ?? 'null') as { version: string; at: number } | null;
      return mark?.version === version && Date.now() - mark.at < RELOAD_GRACE_MS;
    } catch {
      return false;
    }
  }

  function readNotes(): VersionDocument | undefined {
    try {
      const saved = JSON.parse(read(local, keys.notes) ?? 'null') as VersionDocument | null;
      return saved?.version ? saved : undefined;
    } catch {
      return undefined;
    }
  }

  function pendingNotes(): VersionDocument | undefined {
    if (acknowledgedInMemory || read(local, keys.acknowledged) === bundleVersion) return undefined;
    const saved = readNotes();
    return saved?.version === bundleVersion ? saved : { version: bundleVersion, title: "Nuova versione di Travel", items: [`Travel è stato aggiornato alla versione ${bundleVersion}.`] };
  }

  /** Versione nuova: salva le note e ricarica la scheda, una volta sola ogni 5 minuti. */
  function update(release: VersionDocument | undefined): void {
    if (stopped || !release?.version || release.version === bundleVersion || reloadedRecently(release.version)) return;
    write(local, keys.notes, JSON.stringify(release));
    options.beforeUpdate?.();
    write(session, keys.reloadedFor, JSON.stringify({ version: release.version, at: Date.now() }));
    const next = new URL(location.href);
    next.searchParams.set('_v', release.version);
    location.replace(next.toString());
  }

  async function check(): Promise<void> {
    const last = Number(read(local, keys.lastCheck) ?? 0);
    if (Date.now() - last < CHECK_DEDUP_MS) return;
    write(local, keys.lastCheck, String(Date.now()));
    try {
      const response = await fetch(`${options.versionUrl ?? '/version.json'}?ts=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok || stopped) return;
      const current = (await response.json()) as VersionDocument;
      if (stopped) return;
      if (current.version === bundleVersion) {
        if (pendingNotes()) {
          write(local, keys.notes, JSON.stringify(current));
          options.onNotesChange?.(current);
        }
      } else if (current.version) {
        channel?.postMessage({ type: 'available', release: current });
        update(current);
      }
    } catch {
      /* rete assente: nessun falso aggiornamento, si riprova al prossimo evento */
    }
  }

  const onVisible = () => {
    if (!document.hidden) void check();
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== keys.notes && event.key !== keys.acknowledged) return;
    const notes = readNotes();
    if (notes && notes.version !== bundleVersion) update(notes);
    else options.onNotesChange?.(pendingNotes());
  };
  const onMessage = (event: MessageEvent) => {
    const data = event.data as { type?: string; release?: VersionDocument } | undefined;
    if (data?.type === 'available') update(data.release);
  };

  return {
    start(): () => void {
      stopped = false;
      channel = 'BroadcastChannel' in window ? new BroadcastChannel(`${appId}-release`) : undefined;
      channel?.addEventListener('message', onMessage);
      window.addEventListener('storage', onStorage);
      window.addEventListener('focus', onVisible);
      window.addEventListener('online', onVisible);
      document.addEventListener('visibilitychange', onVisible);
      // Il primo controllo ignora la deduplica: una scheda appena aperta deve sapere subito se è vecchia.
      write(local, keys.lastCheck, '0');
      options.onNotesChange?.(pendingNotes());
      void check();
      const timer = window.setInterval(onVisible, 60_000);
      return () => {
        window.clearInterval(timer);
        stopped = true;
        channel?.close();
        window.removeEventListener('storage', onStorage);
        window.removeEventListener('focus', onVisible);
        window.removeEventListener('online', onVisible);
        document.removeEventListener('visibilitychange', onVisible);
      };
    },
    /** Note da mostrare dopo il ricaricamento: solo se riguardano la versione di questo bundle. */
    notes(): VersionDocument | undefined {
      return pendingNotes();
    },
    /** Pulsante «Ho capito»: le note spariscono in tutte le schede. */
    dismissNotes(): void {
      acknowledgedInMemory = true;
      write(local, keys.acknowledged, bundleVersion);
      if (readNotes()?.version === bundleVersion) remove(local, keys.notes);
      options.onNotesChange?.(undefined);
    },
  };
}
